import { Timestamp } from './common';
import {
  FrontDoorDataset,
  FrontDoorStepType,
  VisitExperienceDataset,
} from './front-door';
import { JourneyTriggerStepType, TriggerDataset } from './journey-trigger';

type Id = number;

export enum JourneyType {
  FrontDoor = 'front_door',
  EncounterLookup = 'encounter_lookup',
  InVisit = 'in_visit',
}

export enum BaseJourneyStepType {
  CustomForm = 'custom_form',
  UserValidation = 'user_validation',
  NavigationBlock = 'navigation_block',
}

export enum JourneyActionType {
  Primary = 'primary',
  Secondary = 'secondary',
  Custom = 'custom',
  Conditional = 'conditional',
}

export enum ConditionType {
  WeekdayTimeRange = 'weekday_time_range',
}

export type DateTimeRangeActionCondition = {
  condition_type: ConditionType;
  from_time: string;
  to_time: string;
  week_days: string;
};

export type JourneyStepType =
  BaseJourneyStepType | FrontDoorStepType | JourneyTriggerStepType;
export type JourneyActionCondition = DateTimeRangeActionCondition;

export type JourneyAction = {
  action_type: JourneyActionType;
  condition?: JourneyActionCondition;
  name?: string;
  step_id?: Id;
};

export type CustomFormDataset = {
  custom_form_id?: Id;
  custom_form_version_id?: Id;
};

export type ValidationMethod = 'cdx_channel' | 'access_code_set';

export type ValidationDataset = {
  validation_method?: ValidationMethod;
  validation_tag?: string;
  access_code_set_id?: Id;
  persist_result?: boolean;
  persist_duration_hours?: number;
};

export type BannerDataset = {
  title?: string;
  message?: string;
};

export type CallbackDataset = {
  confirmation_title?: string;
  confirmation_message?: string;
};

export type NavigationBlockButton = {
  name: string;
  icon: string;
  modality: string;
  enabled?: boolean;
};

export type NavigationBlockSection = {
  id: string;
  name: string;
  order: number;
  block_order: string[];
  primary_button: NavigationBlockButton;
  secondary_button: NavigationBlockButton & { enabled: boolean };
};

export type NavigationBlockBlock = {
  id: string;
  name: string;
  description?: string;
  section_id: string;
  order: number;
  request_interpreter?: string;
  video_visit_type_id?: number;
  audio_visit_type_id?: number;
};

export type NavigationBlockDataset = {
  instructional_text?: string;
  tiles_per_section?: number;
  section_order?: string[];
  sections?: Record<string, NavigationBlockSection>;
  blocks?: Record<string, NavigationBlockBlock>;
  hidden_block_ids?: string[];
};

export type JourneyStepDataset = CustomFormDataset &
  VisitExperienceDataset &
  ValidationDataset &
  BannerDataset &
  CallbackDataset &
  NavigationBlockDataset &
  TriggerDataset;

export type JourneyStep = {
  id: Id;
  step_type: JourneyStepType;
  name?: string;
  description?: string;
  actions?: JourneyAction[];
  dataset?: JourneyStepDataset;
  position?: { x: number; y: number };
  show_back_button?: boolean;
  hide_footer_actions?: boolean;
};

export type BaseJourneyDataset = {
  steps: Record<Id, JourneyStep>;
};

export type JourneyDataset = FrontDoorDataset;

export type JourneyStatus = 'active' | 'inactive' | 'draft';

export type Journey = {
  id: Id;
  owner_id: Id;
  status: JourneyStatus;
  name: string;
  title?: string;
  journey_type: JourneyType;
  dataset: JourneyDataset;
  locale?: string;
  created_at?: Timestamp;
  updated_at?: Timestamp;
};
